import axios, { AxiosResponse, AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic API response type
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  pagination?: {
    page: number;
    size: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

// Auth API
export const authApi = {
  login: async (credentials: { email: string; password: string }): Promise<ApiResponse> => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData: {
    name: string;
    email: string;
    password: string;
    role?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  },

  getProfile: async (): Promise<ApiResponse> => {
    const response = await apiClient.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (userData: {
    name: string;
    email: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put('/auth/profile', userData);
    return response.data;
  },

  changePassword: async (passwordData: {
    current_password: string;
    new_password: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put('/auth/change-password', passwordData);
    return response.data;
  },

  logout: async (): Promise<ApiResponse> => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  facebookLogin: async (facebookData: {
    facebookId: string;
    name: string;
    email: string;
    accessToken: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/auth/facebook-login', facebookData);
    return response.data;
  },

  // Email verification for client signup
  sendVerificationEmail: async (userData: {
    name: string;
    email: string;
    password: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/auth/send-verification-email', userData);
    return response.data;
  },

  verifyEmailOTP: async (verificationData: {
    email: string;
    otp: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/auth/verify-email-otp', verificationData);
    return response.data;
  },

  resendVerificationOTP: async (data: {
    email: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/auth/resend-verification-otp', data);
    return response.data;
  }
};

// Peak Collections API
export const peakCollectionsApi = {
  getCollections: async (params?: {
    page?: number;
    size?: number;
    search?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/peak-collections', { params });
    return response.data;
  },

  getCollection: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/peak-collections/${id}`);
    return response.data;
  },

  createCollection: async (data: FormData): Promise<ApiResponse> => {
    const response = await axios.post(`${API_BASE_URL}/adminpanel/peak-collections`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
      }
    });
    return response.data;
  },

  updateCollection: async (id: string, data: FormData): Promise<ApiResponse> => {
    const response = await axios.put(`${API_BASE_URL}/adminpanel/peak-collections/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
      }
    });
    return response.data;
  },

  deleteCollection: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/peak-collections/${id}`);
    return response.data;
  }
};

// Users API
export const usersApi = {
  getUsers: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    role?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/users', { params });
    return response.data;
  },

  getUser: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/users/${id}`);
    return response.data;
  },

  createUser: async (userData: {
    name: string;
    email: string;
    password: string;
    role: string;
    is_active?: boolean;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/users', userData);
    return response.data;
  },

  updateUser: async (id: string, userData: {
    name: string;
    email: string;
    role: string;
    is_active?: boolean;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/users/${id}`, userData);
    return response.data;
  },

  deleteUser: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/users/${id}`);
    return response.data;
  }
};

// Metal Types API
export const metalTypesApi = {
  getMetalTypes: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/metal-types', { params });
    return response.data;
  },

  getMetalType: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/metal-types/${id}`);
    return response.data;
  },

  createMetalType: async (data: {
    title: string;
    image?: string;
    description?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/metal-types', data);
    return response.data;
  },

  updateMetalType: async (id: string, data: {
    title: string;
    image?: string;
    description?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/metal-types/${id}`, data);
    return response.data;
  },

  deleteMetalType: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/metal-types/${id}`);
    return response.data;
  }
};

// Categories API
export const categoriesApi = {
  getCategories: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    category_type_id?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/categories', { params });
    return response.data;
  },

  getCategory: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/categories/${id}`);
    return response.data;
  },

  createCategory: async (data: {
    title: string;
    slug: string;
    image?: string;
    images?: string[];
    category_type_id?: number;
    sub_category_ids?: number[];
    stone_type_ids?: number[];
  } | FormData): Promise<ApiResponse> => {
    // Check if data is FormData
    if (data instanceof FormData) {
      const response = await axios.post(`${API_BASE_URL}/adminpanel/categories`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      // Use regular JSON request
      const response = await apiClient.post('/adminpanel/categories', data);
      return response.data;
    }
  },

  updateCategory: async (id: string, data: {
    title: string;
    slug: string;
    image?: string;
    images?: string[];
    category_type_id?: number;
    sub_category_ids?: number[];
    stone_type_ids?: number[];
    keep_existing_images?: boolean;
  } | FormData): Promise<ApiResponse> => {
    // Check if data is FormData
    if (data instanceof FormData) {
      const response = await axios.put(`${API_BASE_URL}/adminpanel/categories/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      // Use regular JSON request
      const response = await apiClient.put(`/adminpanel/categories/${id}`, data);
      return response.data;
    }
  },

  deleteCategory: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/categories/${id}`);
    return response.data;
  }
};

// Stone Types API
export const stoneTypesApi = {
  getStoneTypes: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/stone-types', { params });
    return response.data;
  },

  getStoneType: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/stone-types/${id}`);
    return response.data;
  },

  createStoneType: async (data: {
    title: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/stone-types', data);
    return response.data;
  },

  updateStoneType: async (id: string, data: {
    title: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/stone-types/${id}`, data);
    return response.data;
  },

  deleteStoneType: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/stone-types/${id}`);
    return response.data;
  }
};

// Carats API
export const caratsApi = {
  getCarats: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/carats', { params });
    return response.data;
  },

  getCarat: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/carats/${id}`);
    return response.data;
  },

  createCarat: async (data: {
    title: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/carats', data);
    return response.data;
  },

  updateCarat: async (id: string, data: {
    title: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/carats/${id}`, data);
    return response.data;
  },

  deleteCarat: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/carats/${id}`);
    return response.data;
  }
};

// Sizes API
export const sizesApi = {
  getSizes: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/sizes', { params });
    return response.data;
  },

  getSize: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/sizes/${id}`);
    return response.data;
  },

  createSize: async (data: {
    title: string;
    mm?: number;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/sizes', data);
    return response.data;
  },

  updateSize: async (id: string, data: {
    title: string;
    mm?: number;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/sizes/${id}`, data);
    return response.data;
  },

  deleteSize: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/sizes/${id}`);
    return response.data;
  }
};

// Occasions API
export const occasionsApi = {
  getOccasions: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/occasions', { params });
    return response.data;
  },

  getOccasion: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/occasions/${id}`);
    return response.data;
  },

  createOccasion: async (data: FormData | {
    title: string;
    image?: File;
  }): Promise<ApiResponse> => {
    if (data instanceof FormData) {
      const response = await axios.post(`${API_BASE_URL}/adminpanel/occasions`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      const response = await apiClient.post('/adminpanel/occasions', data);
      return response.data;
    }
  },

  updateOccasion: async (id: string, data: FormData | {
    title: string;
    image?: File;
  }): Promise<ApiResponse> => {
    if (data instanceof FormData) {
      const response = await axios.put(`${API_BASE_URL}/adminpanel/occasions/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      const response = await apiClient.put(`/adminpanel/occasions/${id}`, data);
      return response.data;
    }
  },

  deleteOccasion: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/occasions/${id}`);
    return response.data;
  }
};

// Weights API
export const weightsApi = {
  getWeights: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/weights', { params });
    return response.data;
  },

  getWeight: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/weights/${id}`);
    return response.data;
  },

  createWeight: async (data: {
    title: string;
    weight_unit?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/weights', data);
    return response.data;
  },

  updateWeight: async (id: string, data: {
    title: string;
    weight_unit?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/weights/${id}`, data);
    return response.data;
  },

  deleteWeight: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/weights/${id}`);
    return response.data;
  }
};

// Genders API
export const gendersApi = {
  getGenders: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/genders', { params });
    return response.data;
  },

  getGender: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/genders/${id}`);
    return response.data;
  },

  createGender: async (data: {
    title: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/genders', data);
    return response.data;
  },

  updateGender: async (id: string, data: {
    title: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/genders/${id}`, data);
    return response.data;
  },

  deleteGender: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/genders/${id}`);
    return response.data;
  }
};

// Products API
export const productsApi = {
  getProducts: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    featured?: boolean;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/products', { params });
    return response.data;
  },

  getProduct: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/products/${id}`);
    return response.data;
  },

  createProduct: async (data: {
    title: string;
    product_code: string;
    description?: string;
    width?: number;
    width_unit?: string;
    height?: number;
    height_unit?: string;
    featured?: boolean;
    gender_ids?: number[];
    occasion_ids?: number[];
    size_ids?: number[];
    carat_ids?: number[];
    weight_ids?: number[];
    category_ids?: number[];
    diamond_categories?: number[];
    diamond_metal_type_ids?: number[];
    diamond_cent_weights?: Array<{ cent: number; charge: number }>;
    peak_collection_ids?: number[];
    images?: File[] | string[];
  }): Promise<ApiResponse> => {
    // Check if we have any File objects in the images array
    const hasFiles = data.images && data.images.length > 0 &&
      data.images.some(img => img instanceof File);

    if (hasFiles) {
      // Use FormData for file uploads
      const formData = new FormData();

      // Add text fields
      formData.append('title', data.title);
      formData.append('product_code', data.product_code);
      if (data.description) formData.append('description', data.description);
      if (data.width) formData.append('width', data.width.toString());
      if (data.width_unit) formData.append('width_unit', data.width_unit);
      if (data.height) formData.append('height', data.height.toString());
      if (data.height_unit) formData.append('height_unit', data.height_unit);
      formData.append('featured', data.featured ? 'true' : 'false');

      // Add JSON arrays as strings
      if (data.gender_ids) formData.append('gender_ids', JSON.stringify(data.gender_ids));
      if (data.occasion_ids) formData.append('occasion_ids', JSON.stringify(data.occasion_ids));
      if (data.size_ids) formData.append('size_ids', JSON.stringify(data.size_ids));
      if (data.carat_ids) formData.append('carat_ids', JSON.stringify(data.carat_ids));
      if (data.weight_ids) formData.append('weight_ids', JSON.stringify(data.weight_ids));
      if (data.category_ids) formData.append('category_ids', JSON.stringify(data.category_ids));
      if (data.diamond_categories) formData.append('diamond_categories', JSON.stringify(data.diamond_categories));
      if (data.diamond_metal_type_ids) formData.append('diamond_metal_type_ids', JSON.stringify(data.diamond_metal_type_ids));
      if (data.diamond_cent_weights) formData.append('diamond_cent_weights', JSON.stringify(data.diamond_cent_weights));
      formData.append('peak_collection_ids', JSON.stringify(data.peak_collection_ids ?? []));

      // Add only File objects, not string URLs
      (data.images as (File | string)[]).forEach(image => {
        if (image instanceof File) {
          formData.append('images', image);
        }
      });

      const fileCount = (data.images as (File | string)[]).filter(img => img instanceof File).length;
      console.log('🚀 Sending FormData with', fileCount, 'files');

      const response = await axios.post(`${API_BASE_URL}/adminpanel/products`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });

      return response.data;
    } else {
      // Use JSON for URL-based images
      const response = await apiClient.post('/adminpanel/products', data);
      return response.data;
    }
  },

  updateProduct: async (id: string, data: {
    title: string;
    product_code: string;
    description?: string;
    width?: number;
    width_unit?: string;
    height?: number;
    height_unit?: string;
    featured?: boolean;
    gender_ids?: number[];
    occasion_ids?: number[];
    size_ids?: number[];
    carat_ids?: number[];
    weight_ids?: number[];
    category_ids?: number[];
    diamond_categories?: number[];
    diamond_metal_type_ids?: number[];
    diamond_cent_weights?: Array<{ cent: number; charge: number }>;
    peak_collection_ids?: number[];
    images?: File[] | string[];
    keep_existing_images?: boolean;
  }): Promise<ApiResponse> => {
    // Check if we have any File objects in the images array
    const hasFiles = data.images && data.images.length > 0 &&
      data.images.some(img => img instanceof File);

    if (hasFiles) {
      // Use FormData for file uploads
      const formData = new FormData();

      // Add text fields
      formData.append('title', data.title);
      formData.append('product_code', data.product_code);
      if (data.description) formData.append('description', data.description);
      if (data.width) formData.append('width', data.width.toString());
      if (data.width_unit) formData.append('width_unit', data.width_unit);
      if (data.height) formData.append('height', data.height.toString());
      if (data.height_unit) formData.append('height_unit', data.height_unit);
      formData.append('featured', data.featured ? 'true' : 'false');

      // Add JSON arrays as strings
      if (data.gender_ids) formData.append('gender_ids', JSON.stringify(data.gender_ids));
      if (data.occasion_ids) formData.append('occasion_ids', JSON.stringify(data.occasion_ids));
      if (data.size_ids) formData.append('size_ids', JSON.stringify(data.size_ids));
      if (data.carat_ids) formData.append('carat_ids', JSON.stringify(data.carat_ids));
      if (data.weight_ids) formData.append('weight_ids', JSON.stringify(data.weight_ids));
      if (data.category_ids) formData.append('category_ids', JSON.stringify(data.category_ids));
      if (data.diamond_categories) formData.append('diamond_categories', JSON.stringify(data.diamond_categories));
      if (data.diamond_metal_type_ids) formData.append('diamond_metal_type_ids', JSON.stringify(data.diamond_metal_type_ids));
      if (data.diamond_cent_weights) formData.append('diamond_cent_weights', JSON.stringify(data.diamond_cent_weights));
      formData.append('peak_collection_ids', JSON.stringify(data.peak_collection_ids ?? []));

      // Add keep existing images flag
      formData.append('keep_existing_images', data.keep_existing_images ? 'true' : 'false');

      // Add only File objects, not string URLs
      (data.images as (File | string)[]).forEach(image => {
        if (image instanceof File) {
          formData.append('images', image);
        }
      });

      const fileCount = (data.images as (File | string)[]).filter(img => img instanceof File).length;
      console.log('🚀 Updating with FormData, files:', fileCount);

      const response = await axios.put(`${API_BASE_URL}/adminpanel/products/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });

      return response.data;
    } else {
      // Use JSON for URL-based images or when no new files
      const response = await apiClient.put(`/adminpanel/products/${id}`, data);
      return response.data;
    }
  },

  deleteProduct: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/products/${id}`);
    return response.data;
  }
};

// Image Upload API
export const imageApi = {
  uploadImages: async (files: FileList | File[]): Promise<ApiResponse<{ imageUrls: string[] }>> => {
    const formData = new FormData();

    // Convert FileList to Array and append each file
    Array.from(files).forEach((file, index) => {
      formData.append('images', file);
    });

    const response = await axios.post(`${API_BASE_URL}/adminpanel/upload/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
      }
    });

    return response.data;
  },

  uploadSingleImage: async (file: File): Promise<ApiResponse<{ imageUrl: string }>> => {
    const formData = new FormData();
    formData.append('images', file); // Backend expects 'images' field for both single and multiple

    const response = await axios.post(`${API_BASE_URL}/adminpanel/upload/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
      }
    });

    return response.data;
  }
};

// Banners API
export const bannersApi = {
  getBanners: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/banners', { params });
    return response.data;
  },

  getBanner: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/banners/${id}`);
    console.log("response", response)
    return response.data;
  },

  createBanner: async (data: FormData | {
    title: string;
    description?: string;
    banner_type?: string;
    image?: File;
  }): Promise<ApiResponse> => {
    if (data instanceof FormData) {
      const response = await axios.post(`${API_BASE_URL}/adminpanel/banners`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      const response = await apiClient.post('/adminpanel/banners', data);
      return response.data;
    }
  },

  updateBanner: async (id: string, data: FormData | {
    title: string;
    description?: string;
    banner_type?: string;
    image?: File;
  }): Promise<ApiResponse> => {
    if (data instanceof FormData) {
      const response = await axios.put(`${API_BASE_URL}/adminpanel/banners/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      const response = await apiClient.put(`/adminpanel/banners/${id}`, data);
      return response.data;
    }
  },

  deleteBanner: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/banners/${id}`);
    return response.data;
  },

  getDropdown: async () => {
    const response = await apiClient.get('/adminpanel/categories');
    return response.data;
  }

};

// CMS Pages API
export const cmsPagesApi = {
  getCmsPages: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    is_active?: boolean;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/cms-pages', { params });
    return response.data;
  },

  getCmsPage: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/cms-pages/${id}`);
    return response.data;
  },

  createCmsPage: async (data: FormData | {
    title: string;
    description?: string;
    is_active?: boolean;
    image?: File;
  }): Promise<ApiResponse> => {
    if (data instanceof FormData) {
      const response = await axios.post(`${API_BASE_URL}/adminpanel/cms-pages`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      const response = await apiClient.post('/adminpanel/cms-pages', data);
      return response.data;
    }
  },

  updateCmsPage: async (id: string, data: FormData | {
    title: string;
    description?: string;
    is_active?: boolean;
    image?: File;
  }): Promise<ApiResponse> => {
    if (data instanceof FormData) {
      const response = await axios.put(`${API_BASE_URL}/adminpanel/cms-pages/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      const response = await apiClient.put(`/adminpanel/cms-pages/${id}`, data);
      return response.data;
    }
  },

  deleteCmsPage: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/cms-pages/${id}`);
    return response.data;
  }
};

// About Us API (dedicated table)
export const aboutUsApi = {
  getAboutUs: async (): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/about-us');
    return response.data;
  },

  saveAboutUs: async (data: {
    hero_title?: string;
    hero_image?: string;
    hero_images?: string[];
    who_we_are_title?: string;
    who_we_are_text?: string;
    mission_title?: string;
    mission_text?: string;
    vision_title?: string;
    vision_text?: string;
    timeline_title?: string;
    timeline_subtitle?: string;
    timeline_text?: string;
    timeline_main_image?: string;
    timeline_overlay_image?: string;
    team_title?: string;
    team_subtitle?: string;
    team_members?: Array<{ name: string; role: string; image: string }>;
    advantage_title?: string;
    advantage_text?: string;
    feature_title?: string;
    feature_text?: string;
    feature_image?: string;
    is_active?: boolean;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/about-us', data);
    return response.data;
  }
};

// Guides API
export const guidesApi = {
  getGuides: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    is_active?: boolean;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/guides', { params });
    return response.data;
  },

  getGuide: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/guides/${id}`);
    return response.data;
  },

  createGuide: async (data: FormData | {
    title: string;
    description?: string;
    is_active?: boolean;
    pdf?: File;
  }): Promise<ApiResponse> => {
    if (data instanceof FormData) {
      const response = await axios.post(`${API_BASE_URL}/adminpanel/guides`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      const response = await apiClient.post('/adminpanel/guides', data);
      return response.data;
    }
  },

  updateGuide: async (id: string, data: FormData | {
    title: string;
    description?: string;
    is_active?: boolean;
    pdf?: File;
  }): Promise<ApiResponse> => {
    if (data instanceof FormData) {
      const response = await axios.put(`${API_BASE_URL}/adminpanel/guides/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      const response = await apiClient.put(`/adminpanel/guides/${id}`, data);
      return response.data;
    }
  },

  deleteGuide: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/guides/${id}`);
    return response.data;
  }
};

// Social Login Settings API
export const socialLoginSettingsApi = {
  getSettings: async (): Promise<ApiResponse<{
    id: number;
    facebook: boolean;
    facebook_client_id: string | null;
    facebook_secrete_key: string | null;
    facebook_callback: string | null;
    facebook_app_secret: string | null;
    google: boolean;
    google_client_id: string | null;
    google_secrete_key: string | null;
    google_callback: string | null;
    google_client_secret: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }>> => {
    const response = await apiClient.get('/social-login-settings');
    return response.data;
  },

  updateSettings: async (data: {
    facebook: boolean;
    facebook_client_id?: string;
    facebook_secrete_key?: string;
    facebook_callback?: string;
    facebook_app_secret?: string;
    google: boolean;
    google_client_id?: string;
    google_secrete_key?: string;
    google_callback?: string;
    google_client_secret?: string;
  }): Promise<ApiResponse<{
    id: number;
    facebook: boolean;
    facebook_client_id: string | null;
    facebook_secrete_key: string | null;
    facebook_callback: string | null;
    facebook_app_secret: string | null;
    google: boolean;
    google_client_id: string | null;
    google_secrete_key: string | null;
    google_callback: string | null;
    google_client_secret: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }>> => {
    const response = await apiClient.post('/social-login-settings', data);
    return response.data;
  },

  getPublicSettings: async (): Promise<ApiResponse<{
    id: number;
    facebook: boolean;
    google: boolean;
    created_at: string;
    updated_at: string;
  }>> => {
    const response = await apiClient.get('/social-login-settings/public');
    return response.data;
  }
};

// =====================
// PGM Videos API
// =====================
export const pgmVideosApi = {
  getPGMVideos: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/pgm-videos', { params });
    return response.data;
  },

  getPGMVideo: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/pgm-videos/${id}`);
    return response.data;
  },

  createPGMVideo: async (data: FormData | {
    title: string;
    description?: string;
    video?: File;
  }): Promise<ApiResponse> => {
    if (data instanceof FormData) {
      const response = await axios.post(`${API_BASE_URL}/adminpanel/pgm-videos`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      const response = await apiClient.post('/adminpanel/pgm-videos', data);
      return response.data;
    }
  },

  updatePGMVideo: async (id: string, data: FormData | {
    title: string;
    description?: string;
    video?: File;
  }): Promise<ApiResponse> => {
    if (data instanceof FormData) {
      const response = await axios.put(`${API_BASE_URL}/adminpanel/pgm-videos/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      return response.data;
    } else {
      const response = await apiClient.put(`/adminpanel/pgm-videos/${id}`, data);
      return response.data;
    }
  },

  deletePGMVideo: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/pgm-videos/${id}`);
    return response.data;
  }
};

// =====================
// Diamond Metal Types API
// =====================
export const diamondMetalTypesApi = {
  getDiamondMetalTypes: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/diamond-metal-types/admin', { params });
    return response.data;
  },

  getDiamondMetalType: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/diamond-metal-types/admin/${id}`);
    return response.data;
  },

  createDiamondMetalType: async (data: {
    title: string;
    slug?: string;
    type: string;
    description?: string;
    is_active?: boolean;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/diamond-metal-types/admin', data);
    return response.data;
  },

  updateDiamondMetalType: async (id: string, data: {
    title?: string;
    slug?: string;
    type?: string;
    description?: string;
    is_active?: boolean;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/diamond-metal-types/admin/${id}`, data);
    return response.data;
  },

  deleteDiamondMetalType: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/diamond-metal-types/admin/${id}`);
    return response.data;
  }
};

// =====================
// Diamond Categories API
// =====================
export const diamondCategoriesApi = {
  getDiamondCategories: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/diamond-categories', { params });
    return response.data;
  },

  getDiamondCategory: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/diamond-categories/${id}`);
    return response.data;
  },

  createDiamondCategory: async (data: {
    title: string;
    type?: string;
    price?: number;
    unit?: string;
    pre_carat?: number;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/diamond-categories', data);
    return response.data;
  },

  updateDiamondCategory: async (id: string, data: {
    title: string;
    type?: string;
    price?: number;
    unit?: string;
    pre_carat?: number;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/diamond-categories/${id}`, data);
    return response.data;
  },

  deleteDiamondCategory: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/diamond-categories/${id}`);
    return response.data;
  }
};

// =====================
// Diamond Category Prices API
// =====================
export const diamondPricesApi = {
  getPricesByCategory: async (categoryId: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/diamond-category-prices/category/${categoryId}`);
    return response.data;
  },

  getPrice: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/diamond-category-prices/${id}`);
    return response.data;
  },

  createPrice: async (data: {
    diamond_category_id: number;
    category_name: string;
    price: number;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/diamond-category-prices', data);
    return response.data;
  },

  updatePrice: async (id: string, data: {
    category_name: string;
    price: number;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/diamond-category-prices/${id}`, data);
    return response.data;
  },

  deletePrice: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/diamond-category-prices/${id}`);
    return response.data;
  }
};

// =====================
// Elite Plans API
// =====================
export const elitePlansApi = {
  getElitePlans: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/elite-plan', { params });
    return response.data;
  },

  getElitePlan: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/elite-plan/${id}`);
    return response.data;
  },

  createElitePlan: async (data: {
    plan_name: string;
    plan_slug: string;
    plan_days: number;
    plan_price: number;
    plan_description?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/elite-plan/store', data);
    return response.data;
  },

  updateElitePlan: async (
    id: string,
    data: {
      plan_name: string;
      plan_slug: string;
      plan_days: number;
      plan_price: number;
      plan_description?: string;
    }
  ): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/elite-plan/update/${id}`, data);
    return response.data;
  },

  deleteElitePlan: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/elite-plan/delete/${id}`);
    return response.data;
  }
};

// =====================
// Price Management API
// =====================
export const priceManagementApi = {
  getPrices: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/price-management', { params });
    return response.data;
  },

  getPrice: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/price-management/${id}`);
    return response.data;
  },

  createPrice: async (data: {
    title: string;
    type?: string;
    metal_type?: string;
    current_price: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/price-management', data);
    return response.data;
  },

  updatePrice: async (
    id: string,
    data: {
      title: string;
      type?: string;
      metal_type?: string;
      current_price: string;
    }
  ): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/price-management/${id}`, data);
    return response.data;
  },

  deletePrice: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/price-management/${id}`);
    return response.data;
  }
};

// Schedule Calls API
export const scheduleCallsApi = {
  getScheduleCalls: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortOrder?: string;
    status?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/schedule-calls', { params });
    return response.data;
  },

  updateScheduleCallStatus: async (id: string, data: { status: string }): Promise<ApiResponse> => {
    const response = await apiClient.patch(`/adminpanel/schedule-calls/${id}/status`, data);
    return response.data;
  }
};

// Promo Codes API
export const promoCodesApi = {
  getPromoCodes: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/promocodes', { params });
    return response.data;
  },

  getPromoCode: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/promocodes/${id}`);
    return response.data;
  },

  createPromoCode: async (data: {
    promo_code: string;
    promo_code_type: string;
    promo_code_amount: number;
    user_max_usage: number;
    valid_start_time: string;
    valid_end_time: string;
    category?: number[] | string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/adminpanel/promocodes', data);
    return response.data;
  },

  updatePromoCode: async (id: string, data: {
    promo_code?: string;
    promo_code_type?: string;
    promo_code_amount?: number;
    user_max_usage?: number;
    valid_start_time?: string;
    valid_end_time?: string;
    category?: number[] | string;
    isActive?: boolean;
  }): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/promocodes/${id}`, data);
    return response.data;
  },

  deletePromoCode: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete(`/adminpanel/promocodes/${id}`);
    return response.data;
  }
};

// =====================
// Elite Members API
// =====================
export const eliteMembersApi = {
  getEliteMembers: async (params?: {
    page?: number;
    size?: number;
    search?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/elite-members', { params });
    return response.data;
  }
};

// =====================
// Orders API
// =====================
export const ordersApi = {
  getOrders: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    order_status?: string;
    payment_status?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.get('/adminpanel/orders', { params });
    return response.data;
  },

  getOrder: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.get(`/adminpanel/orders/${id}`);
    return response.data;
  },

  updateOrderStatus: async (id: string, order_status: string): Promise<ApiResponse> => {
    const response = await apiClient.put(`/adminpanel/orders/${id}/status`, { order_status });
    return response.data;
  }
};

export default apiClient;
